import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { X } from 'lucide-react-native';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, Easing } from 'react-native';

import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { useColors } from '@/hooks/useColors';
import { getAccessibilityProps } from '@/util/accessibilityProps';

const ENTRANCE_DURATION_MS = 300;

const EXIT_DURATION_MS = 220;

const ENTRANCE_OFFSET_Y = 8;

const EXIT_OFFSET_Y = -6;

type InboxFeatureBannerProps = {
  badgeLabel: string;
  title: string;
  description: string;
  dismissAccessibilityLabel: string;
  onDismiss: () => void;
};

export function InboxFeatureBanner({
  badgeLabel,
  title,
  description,
  dismissAccessibilityLabel,
  onDismiss,
}: InboxFeatureBannerProps) {
  const { colors } = useColors();

  const opacity = useRef(new Animated.Value(0)).current;

  const translateY = useRef(new Animated.Value(ENTRANCE_OFFSET_Y)).current;

  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    const entranceAnimation = Animated.parallel([
      Animated.timing(opacity, {
        toValue: 1,
        duration: ENTRANCE_DURATION_MS,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(translateY, {
        toValue: 0,
        duration: ENTRANCE_DURATION_MS,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
    ]);

    entranceAnimation.start();
  }, [opacity, translateY]);

  const handleDismiss = useCallback(() => {
    if (isClosing) {
      return;
    }

    setIsClosing(true);

    const closeAnimation = Animated.parallel([
      Animated.timing(opacity, {
        toValue: 0,
        duration: EXIT_DURATION_MS,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(translateY, {
        toValue: EXIT_OFFSET_Y,
        duration: EXIT_DURATION_MS,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: true,
      }),
    ]);

    closeAnimation.start(() => {
      onDismiss();
    });
  }, [isClosing, onDismiss, opacity, translateY]);

  return (
    <Animated.View
      style={{
        opacity,
        transform: [{ translateY }],
      }}>
      <VStack className="relative overflow-hidden border border-b-0 border-outline-200 bg-background-0 px-4 py-3.5 shadow-md transition-all duration-150 ease-out web:hover:translate-y-[1px] web:hover:border-b web:hover:border-outline-200 web:hover:shadow-none">
        <VStack className="absolute left-0 right-0 top-0 h-px bg-background-50" />
        <VStack className="absolute bottom-0 left-0 top-0 w-1 bg-primary-500" />
        <Pressable
          {...getAccessibilityProps({ role: 'button', label: dismissAccessibilityLabel })}
          className="absolute right-1 top-1 z-10 min-h-11 min-w-11 cursor-pointer items-center justify-center rounded-sm p-1.5 web:cursor-pointer"
          style={isWeb ? ({ cursor: 'pointer', zIndex: 10 } as never) : ({ zIndex: 10 } as never)}
          onPress={handleDismiss}>
          <Icon
            as={X}
            size="md"
            className="cursor-pointer text-typography-900 web:cursor-pointer"
          />
        </Pressable>

        <HStack className="items-start gap-3 pr-12">
          <Animated.View
            style={{
              marginTop: 2,
              alignSelf: 'flex-start',
              borderRadius: 6,
              paddingHorizontal: 8,
              paddingVertical: 4,
              backgroundColor: colors.primary[100],
            }}>
            <Text size="xs" className="font-semibold uppercase tracking-wide text-primary-700">
              {badgeLabel}
            </Text>
          </Animated.View>
          <VStack className="min-w-0 flex-1">
            <Text size="md" className="font-semibold text-typography-900">
              {title}
            </Text>
            <Text size="sm" className="mt-0.5 text-typography-700">
              {description}
            </Text>
          </VStack>
        </HStack>
      </VStack>
    </Animated.View>
  );
}
